import { Collapse, Row } from "antd";
import type { CollapseProps } from "antd";
import { FiscalPeriod } from "../repository/data.default";
import {
  getCounsellingRequests,
  getIncidents,
  getUserLoginHistory,
  getWellbeingReports,
} from "../repository/data.repository";
import MetricChart, { MetricDescriptor } from "./analytics/MetricChart";

// Company analytics, fully driven by the reusable MetricChart abstraction: each chart is a declarative metric whose
// range can be a window (all-time / preset / a fiscal period / custom) or a period comparison (e.g. logins per quarter).
export interface CompanyViewChartsProps {
  readonly companyUid: string;
  readonly fiscalPeriods: FiscalPeriod[];
}

const logins: MetricDescriptor = {
  key: "logins",
  title: "Logins",
  fetch: getUserLoginHistory,
  dateField: "timestamp",
  display: { kind: "trend", valueLabel: "Logins" },
};

const incidentsByDept: MetricDescriptor = {
  key: "incidentsDept",
  title: "Incidents by Department",
  fetch: getIncidents,
  dateField: "createdOn",
  display: { kind: "breakdown", groupField: "departmentName", chart: "pie" },
};

const incidentsByStatus: MetricDescriptor = {
  key: "incidentsStatus",
  title: "Incidents by Status",
  fetch: getIncidents,
  dateField: "createdOn",
  display: { kind: "breakdown", groupField: "status", chart: "ring" },
};

const counsellingByTherapist: MetricDescriptor = {
  key: "counselling",
  title: "Counselling by Therapist",
  fetch: getCounsellingRequests,
  dateField: "createdOn",
  display: { kind: "breakdown", groupField: "therapistUserName", chart: "pie" },
};

const reportsByAuthor: MetricDescriptor = {
  key: "reports",
  title: "Reports by Author",
  fetch: getWellbeingReports,
  dateField: "createdOn",
  display: { kind: "breakdown", groupField: "createdByUserUserName", chart: "column", valueLabel: "Reports" },
};

function CompanyViewCharts(props: CompanyViewChartsProps) {
  const chart = (metric: MetricDescriptor) => (
    <MetricChart
      companyUid={props.companyUid}
      fiscalPeriods={props.fiscalPeriods}
      metric={metric}
    />
  );

  const items: CollapseProps["items"] = [
    { key: "1", label: "LOGINS", children: chart(logins), style: { fontSize: "1rem" } },
    {
      key: "2",
      label: "INCIDENTS OVERVIEW",
      children: (
        <Row className="flex flex-row justify-around gap-2">
          <div className="basis-[48%]">{chart(incidentsByDept)}</div>
          <div className="basis-[48%]">{chart(incidentsByStatus)}</div>
        </Row>
      ),
      style: { fontSize: "1rem" },
    },
    {
      key: "3",
      label: "COLLABORATORS OVERVIEW",
      children: (
        <Row className="flex flex-row justify-around gap-2">
          <div className="basis-[48%]">{chart(counsellingByTherapist)}</div>
          <div className="basis-[48%]">{chart(reportsByAuthor)}</div>
        </Row>
      ),
      style: { fontSize: "1rem" },
    },
  ];

  return <Collapse destroyInactivePanel accordion items={items} />;
}

export default CompanyViewCharts;
